/** Небольшой стор на context + useReducer: проект, настройки, загрузки, поток статусов. */
import { createContext, useContext, useMemo, useReducer } from 'react';
import type { Dispatch, ReactElement, ReactNode } from 'react';
import { DEFAULT_SETTINGS } from '@reels/shared/constants';
import type { AssetKind, ProjectSettings, ProjectStatus } from '@reels/shared';
import type { StreamMode } from '../lib/events';
import { readOnboardingSeen } from '../lib/prefs';
import type { AuthUser, MeResponse, ProjectDetail, ProjectSummary } from '../lib/types';

export type Screen =
  | 'onboarding'
  | 'editor'
  | 'progress'
  | 'result'
  | 'projects'
  | 'profile'
  | 'support'
  | 'balance'
  | 'limit'
  | 'invite';

export type UploadStatus = 'checking' | 'queued' | 'uploading' | 'done' | 'error' | 'canceled';

export interface UploadItem {
  position?: number;
  thumbUrl?: string;
  /** Локальный идентификатор до появления assetId. */
  id: string;
  slot: 'reaction' | 'illustration';
  kind: AssetKind;
  name: string;
  bytes: number;
  status: UploadStatus;
  /** 0..1 */
  progress: number;
  uploadedBytes: number;
  durationS: number | null;
  width: number | null;
  height: number | null;
  assetId: string | null;
  error: string | null;
  resumed: boolean;
}

export interface StageState {
  completedFiles?: number;
  totalFiles?: number;
  status: ProjectStatus;
  stage: string;
  progress: number;
}

export interface State {
  booting: boolean;
  bootError: string | null;
  user: AuthUser | null;
  online: boolean;
  onboardingSeen: boolean;
  route: Screen | null;
  project: ProjectDetail | null;
  projectLoading: boolean;
  projects: ProjectSummary[] | null;
  projectsLoading: boolean;
  projectsError: string | null;
  profile: MeResponse | null;
  profileLoading: boolean;
  profileError: string | null;
  settings: ProjectSettings;
  uploads: UploadItem[];
  stage: StageState | null;
  streamMode: StreamMode | null;
  toast: string | null;
  busy: boolean;
  activity: Record<string, ProjectSummary & { progress?: number | null; error?: string | null }>;
  unreadProjects: string[];
}

export const initialState: State = {
  booting: true,
  bootError: null,
  user: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  onboardingSeen: readOnboardingSeen(),
  route: null,
  project: null,
  projectLoading: false,
  projects: null,
  projectsLoading: false,
  projectsError: null,
  profile: null,
  profileLoading: false,
  profileError: null,
  settings: { ...DEFAULT_SETTINGS },
  uploads: [],
  stage: null,
  streamMode: null,
  toast: null,
  busy: false,
  activity: {},
  unreadProjects: [],
};

export type Action =
  | { type: 'boot/start' }
  | { type: 'boot/success'; user: AuthUser }
  | { type: 'boot/error'; message: string }
  | { type: 'online'; value: boolean }
  | { type: 'route'; screen: Screen | null }
  | { type: 'onboarding/seen' }
  | { type: 'projects/loading' }
  | { type: 'projects/loaded'; items: ProjectSummary[] }
  | { type: 'projects/error'; message: string }
  | { type: 'profile/loading' }
  | { type: 'profile/loaded'; profile: MeResponse }
  | { type: 'profile/error'; message: string }
  | { type: 'project/loading'; value: boolean }
  | { type: 'project/set'; project: ProjectDetail | null }
  | { type: 'project/created'; project: ProjectDetail }
  | { type: 'activity/update'; id: string; patch: Partial<ProjectDetail>; stage?: StageState }
  | { type: 'activity/read'; id: string }
  | { type: 'activity/remove'; id: string }
  | { type: 'project/merge'; patch: Partial<ProjectDetail> }
  | { type: 'settings/set'; settings: ProjectSettings }
  /** Частичное обновление: сливается в редьюсере, поэтому быстрые правки подряд не теряются. */
  | { type: 'settings/patch'; patch: Partial<ProjectSettings> }
  | { type: 'uploads/add'; items: UploadItem[] }
  | { type: 'uploads/update'; id: string; patch: Partial<UploadItem> }
  | { type: 'uploads/remove'; id: string }
  | { type: 'uploads/clear' }
  | { type: 'stage/set'; stage: StageState | null }
  | { type: 'stream/mode'; mode: StreamMode | null }
  | { type: 'toast'; message: string | null }
  | { type: 'busy'; value: boolean };

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'boot/start':
      return { ...state, booting: true, bootError: null };
    case 'boot/success':
      return {
        ...state,
        booting: false,
        bootError: null,
        user: action.user,
        // Серверный флаг главнее: локальный теряется вместе с адресом приложения.
        onboardingSeen: state.onboardingSeen || action.user.onboardingSeen === true,
      };
    case 'boot/error':
      return { ...state, booting: false, bootError: action.message };
    case 'online':
      return { ...state, online: action.value };
    case 'route':
      return { ...state, route: action.screen };
    case 'onboarding/seen':
      return { ...state, onboardingSeen: true };
    case 'projects/loading':
      return { ...state, projectsLoading: true, projectsError: null };
    case 'projects/loaded':
      return { ...state, projectsLoading: false, projects: action.items, projectsError: null };
    case 'projects/error':
      return { ...state, projectsLoading: false, projectsError: action.message };
    case 'profile/loading':
      return { ...state, profileLoading: true, profileError: null };
    case 'profile/loaded':
      return { ...state, profileLoading: false, profile: action.profile, profileError: null };
    case 'profile/error':
      return { ...state, profileLoading: false, profileError: action.message };
    case 'project/loading':
      return { ...state, projectLoading: action.value };
    case 'project/set':
      return {
        ...state,
        project: action.project,
        settings: action.project?.settings ?? state.settings,
        projectLoading: false,
        stage: null,
      };
    case 'project/created':
      // Settings remain editable while the draft is being created.
      return { ...state, project: { ...action.project, settings: state.settings }, projectLoading: false, stage: null };
    case 'activity/update': {
      const current = state.project?.id === action.id;
      const previous = state.activity[action.id] ?? (current ? state.project : state.projects?.find(item => item.id === action.id));
      if (!previous) return state;
      const updated = { ...previous, ...action.patch };
      const completed = updated.status === 'done' && previous.status !== 'done';
      return {
        ...state,
        activity: { ...state.activity, [action.id]: updated },
        project: current && state.project ? { ...state.project, ...action.patch } : state.project,
        projects: state.projects?.map(item => item.id === action.id ? { ...item, ...action.patch } : item) ?? null,
        stage: current && action.stage ? action.stage : state.stage,
        unreadProjects: completed ? [...new Set([...state.unreadProjects, action.id])] : state.unreadProjects,
      };
    }
    case 'activity/read':
      return { ...state, unreadProjects: state.unreadProjects.filter(id => id !== action.id) };
    case 'activity/remove': {
      const activity = { ...state.activity };
      delete activity[action.id];
      return { ...state, activity, unreadProjects: state.unreadProjects.filter(id => id !== action.id) };
    }
    case 'project/merge':
      return state.project ? { ...state, project: { ...state.project, ...action.patch } } : state;
    case 'settings/set':
      return { ...state, settings: action.settings };
    case 'settings/patch':
      return { ...state, settings: { ...state.settings, ...action.patch } };
    case 'uploads/add':
      return { ...state, uploads: [...state.uploads, ...action.items] };
    case 'uploads/update':
      return {
        ...state,
        uploads: state.uploads.map((item) =>
          item.id === action.id ? { ...item, ...action.patch } : item,
        ),
      };
    case 'uploads/remove':
      return { ...state, uploads: state.uploads.filter((item) => item.id !== action.id) };
    case 'uploads/clear':
      return { ...state, uploads: [] };
    case 'stage/set':
      return { ...state, stage: action.stage };
    case 'stream/mode':
      return { ...state, streamMode: action.mode };
    case 'toast':
      return { ...state, toast: action.message };
    case 'busy':
      return { ...state, busy: action.value };
    default:
      return state;
  }
}

export interface StoreValue {
  state: State;
  dispatch: Dispatch<Action>;
}

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo<StoreValue>(() => ({ state, dispatch }), [state]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error('useStore вызван вне StoreProvider');
  return value;
}
